import { createAsyncThunk } from '@reduxjs/toolkit';
import { apiGet } from 'api-service';
import { apiPost, apiPut } from 'api-service/index';
import translation from 'translation';
import { mapPlatformToPath } from 'utils';
import { isArray, toast } from 'lib';
import {
  transformFetchBlackListResourcesResponse,
  transformFetchBlackListResponse,
  transformSearchResourcesResponse,
  prepareBlackListResourcesForSave,
  prepareSearchResourcesRequest,
} from './helper';

const { t } = translation;

export const fetchBlackList = createAsyncThunk(
  'blackListReducer/fetchBlackList',
  async (_, thunkAPI) => {
    try {
      const response = await apiGet('/search/blacklist');

      return transformFetchBlackListResponse(response.data.result.data);
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const fetchBlackListResources = createAsyncThunk(
  'blackListReducer/fetchBlackListResources',
  async ({ onlyIds, ...params } = {}, thunkAPI) => {
    const { settingsReducer } = thunkAPI.getState();
    try {
      const response = await apiGet('/search/blacklist/resources', {
        page: 1,
        count:
          settingsReducer?.filters?.public?.pageContentCountSlider
            ?.selectedValue,
        ...params,
      });

      return transformFetchBlackListResourcesResponse(
        response,
        params?.platform ?? undefined
      );
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const searchResources = createAsyncThunk(
  'blackListReducer/searchResources',
  async (params = {}, thunkAPI) => {
    const { settingsReducer, blackListReducer } = thunkAPI.getState();

    const prepareParams = prepareSearchResourcesRequest(
      blackListReducer,
      settingsReducer,
      params
    );

    try {
      const response = await apiGet('/search/api/v1/search', prepareParams);

      return transformSearchResourcesResponse(
        response,
        blackListReducer?.selectedPlatformForResources
      );
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const saveBlackListResources = createAsyncThunk(
  'blackListReducer/saveBlackListResources',
  async (_, thunkAPI) => {
    const { blackListReducer } = thunkAPI.getState();
    try {
      const params = prepareBlackListResourcesForSave(
        blackListReducer.method,
        blackListReducer?.addedResources,
        blackListReducer?.title,
        blackListReducer?.description
      );

      let response;
      if (blackListReducer.method === 'post')
        response = await apiPost('/search/blacklist', params);
      else response = await apiPut('/search/blacklist', params);

      toast({
        title: t('vipResources:success.saveBlackListResources.title'),
        description: t(
          'vipResources:success.saveBlackListResources.description'
        ),
      });

      return response?.data?.result?.data;
    } catch (error) {
      toast({
        type: 'error',
        title: t('vipResources:error.saveBlackListResources.title'),
        description: t('vipResources:error.saveBlackListResources.description'),
      });
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const addToBlacklist = createAsyncThunk(
  'blacklist/addToBlacklist',
  async ({ platform, ids, isBackendTableCreatedBefore }, thunkAPI) => {
    try {
      const response = isBackendTableCreatedBefore
        ? await apiPut('/search/blacklist', {
            add_resources: [
              {
                platform: mapPlatformToPath(platform),
                ids,
              },
            ],
          })
        : await apiPost('/search/blacklist', {
            resources: [
              {
                platform: mapPlatformToPath(platform),
                ids,
              },
            ],
          });
      toast({
        title: t('common:successOperation'),
        description: t('profile:success.addToBlacklist.description'),
      });
      return response?.data?.result?.data;
    } catch (error) {
      toast({
        type: 'error',
        title: t('common:errorOperation'),
        description: t('profile:error.addToBlacklist.description'),
      });
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const removeFromBlacklist = createAsyncThunk(
  'blacklist/removeFromBlacklist',
  async ({ platform, ids }, thunkAPI) => {
    try {
      const { selectedBlacklistIds } = thunkAPI.getState().blackListReducer;

      const response = await apiPut('/search/blacklist', {
        delete_resources: [
          {
            platform: mapPlatformToPath(platform),
            ids: isArray(ids) ? ids : selectedBlacklistIds,
          },
        ],
      });
      toast({
        title: t('common:successOperation'),
        description: t('profile:success.removeFromBlacklist.description'),
      });
      return response?.data?.result?.data;
    } catch (error) {
      toast({
        type: 'error',
        title: t('common:errorOperation'),
        description: t('profile:error.removeFromBlacklist.description'),
      });
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const existsInBlacklist = createAsyncThunk(
  'blacklist/existsInBlacklist',
  async ({ resourceId }, thunkAPI) => {
    try {
      const response = await apiGet(
        `/search/blacklist/resources/${resourceId}`
      );
      return response?.data?.result?.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);
